/**
 * Designations Create Page — hardcoded form.
 */

import * as React from 'react';
import { useNavigate } from 'react-router';
import { useCreate } from '@refinedev/core';
import {
  Button, Card, CardContent, Input, Label, Textarea, Checkbox,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

export function DesignationsCreatePage() {
  const navigate = useNavigate();
  const { mutate: create, isLoading } = useCreate();
  const [form, setForm] = React.useState({
    title: '', grade: '', level: 1, min_salary: '', max_salary: '', description: '', is_active: true,
  });
  const [error, setError] = React.useState<string | null>(null);
  const set = (f: string, v: unknown) => setForm(p => ({ ...p, [f]: v }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    create(
      { resource: 'designations', values: form },
      {
        onSuccess: () => navigate('/designations'),
        onError: (err: any) => setError(err?.message ?? 'Failed to create designation. Please try again.'),
      },
    );
  }

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/designations')}><ArrowLeft className="h-4 w-4" /></Button>
        <h1 className="text-2xl font-semibold">Create Designation</h1>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="title">Title *</Label>
              <Input id="title" value={form.title} onChange={e => set('title', e.target.value)} required placeholder="e.g. Software Engineer" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="grade">Grade</Label>
                <Input id="grade" value={form.grade} onChange={e => set('grade', e.target.value)} placeholder="e.g. L3, Senior" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="level">Level</Label>
                <Input id="level" type="number" min={1} value={form.level} onChange={e => set('level', parseInt(e.target.value) || 1)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="min_salary">Min Salary</Label>
                <Input id="min_salary" type="number" min={0} value={form.min_salary} onChange={e => set('min_salary', e.target.value)} placeholder="0.00" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="max_salary">Max Salary</Label>
                <Input id="max_salary" type="number" min={0} value={form.max_salary} onChange={e => set('max_salary', e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={form.description} onChange={e => set('description', e.target.value)} rows={3} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.is_active} onCheckedChange={v => set('is_active', v)} />
              <span className="text-sm">Active</span>
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate('/designations')}>Cancel</Button>
              <Button type="submit" disabled={isLoading}>{isLoading ? 'Creating...' : 'Create Designation'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default DesignationsCreatePage;
